import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  X,
  ArrowsClockwise,
  Check,
  Funnel,
  Plus,
} from '@phosphor-icons/react';
import { useAppStore } from '@/store/appStore';

export function BottomBar() {
  const search = useAppStore((s) => s.search);
  const setSearch = useAppStore((s) => s.setSearch);
  const setCreating = useAppStore((s) => s.setCreatingTask);
  const setFiltersOpen = useAppStore((s) => s.setFiltersOpen);
  const navMenuOpen = useAppStore((s) => s.navMenuOpen);
  const setNavMenuOpen = useAppStore((s) => s.setNavMenuOpen);
  const filters = useAppStore((s) => s.filters);
  const sitePlanFilters = useAppStore((s) => s.sitePlanFilters);
  const qc = useQueryClient();
  const location = useLocation();

  const [refreshState, setRefreshState] = useState<'idle' | 'refreshing' | 'done'>('idle');

  const isSitePlan = location.pathname === '/site-plan';
  const isTramites = location.pathname === '/tramites';
  // Formaleta trae su propio buscador, sus propios chips de filtro y su propio
  // FAB («+ Inventariar»), asi que las tres piezas de tareas de esta barra no
  // solo sobran: enganan. El «+ Agregar» de aca abre el TaskModal, o sea que
  // junto al FAB verde quedaban dos botones de «agregar» pegados haciendo cosas
  // distintas, y el input escribia en el `search` de tareas, que esta pantalla
  // ni lee. Se le dejan solo el menu y el refresh —matar la barra entera la
  // dejaria sin navegacion, como advierte el comentario de Shell en App.tsx.
  const isFormaleta = location.pathname === '/formaleta';

  // Conteo de filtros activos (misma logica que tenia el TopBar antes).
  const activeFilterCount = isSitePlan
    ? Object.values(sitePlanFilters).filter((v) => v !== null).length
    : Object.entries(filters).filter(([k, v]) => {
        if (k === 'authStatus') return v !== 'all';
        if (k === 'activoStatus') return v !== 'active';
        if (k === 'dateField') return false;
        if (k === 'filterMonth') return false;
        return v !== null;
      }).length;

  async function handleRefresh() {
    if (refreshState === 'refreshing') return;
    setRefreshState('refreshing');
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tasks'] }),
        qc.invalidateQueries({ queryKey: ['catalogs'] }),
        qc.invalidateQueries({ queryKey: ['bitacora'] }),
        qc.invalidateQueries({ queryKey: ['checklist'] }),
        qc.invalidateQueries({ queryKey: ['lotes-svg'] }),
      ]);
    } finally {
      setRefreshState('done');
      setTimeout(() => setRefreshState('idle'), 2000);
    }
  }

  return (
    // Dos cosas de una, las dos del reporte del 21-oct-2026.
    //
    // La fila baja de 64 a 48 px —la misma altura que la de arriba, que es lo
    // que se esperaba de entrada—: los botones ya medían 44 y el search 40, así
    // que los 16 px que sobraban no eran de nadie. Era el «sobra demasiado
    // espacio» de Daniel.
    //
    // Y lo que se gana ahí NO se regala abajo: el `pb` del recorte es donde el
    // teléfono dibuja el indicador de inicio. Medido en un 17 Pro Max, la
    // hamburguesa terminaba 24 px DENTRO de esa zona. Lo que parecía margen era
    // una barra que el dedo no alcanzaba del todo.
    //
    // El fondo oscuro envuelve a los dos —fila y recorte— para que no quede la
    // franja blanca de abajo que se ve en su video.
    <div data-franja="abajo" className="bg-brand-gray-500 pb-[var(--franja-abajo)] text-white">
    <div className="flex h-12 items-center gap-2 px-3">
      {/* Hamburger animado (3 spans que rotan / fade) */}
      <button
        type="button"
        onClick={() => setNavMenuOpen(!navMenuOpen)}
        aria-label={navMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
        className="flex h-10 w-10 items-center justify-center rounded hover:bg-brand-gray-400 active:bg-brand-gray-400"
      >
        <span className="relative block h-4 w-6">
          <span
            className={`absolute left-0 h-0.5 w-6 bg-white transition-all duration-300 ease-in-out ${
              navMenuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
            }`}
          />
          <span
            className={`absolute left-0 top-1/2 h-0.5 w-6 -translate-y-1/2 bg-white transition-opacity duration-200 ${
              navMenuOpen ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <span
            className={`absolute left-0 h-0.5 w-6 bg-white transition-all duration-300 ease-in-out ${
              navMenuOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'
            }`}
          />
        </span>
      </button>

      {/* Refresh con feedback idle/spin/check verde */}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshState === 'refreshing'}
        aria-label="Actualizar"
        title={
          refreshState === 'refreshing'
            ? 'Actualizando…'
            : refreshState === 'done'
            ? 'Actualizado'
            : 'Actualizar'
        }
        className="flex h-11 w-11 items-center justify-center rounded hover:bg-brand-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {refreshState === 'done' ? (
          <Check size={18} weight="bold" className="text-brand-green-100" />
        ) : (
          <ArrowsClockwise
            size={18}
            weight="bold"
            className={refreshState === 'refreshing' ? 'animate-spin' : ''}
          />
        )}
      </button>

      {/* Search centrado, max-w para que no domine en pantallas grandes */}
      <div className={`relative mx-auto flex-1 max-w-sm ${isFormaleta ? 'invisible' : ''}`}>
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isSitePlan ? 'Buscar lote / cliente / modelo / banco' : 'Buscar en tarea'}
          className="h-10 w-full rounded bg-white pl-10 pr-10 text-sm text-brand-gray-500 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-green-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Limpiar búsqueda"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {/* Slot derecho: Filtrar (siempre) + Agregar (solo vistas de tareas).
          En mobile (<sm) son icon-only para no comerse el ancho del search;
          en sm+ vuelven a mostrar el label de texto. El badge de filtros
          activos en mobile se pega arriba-derecha como notification dot. */}
      {!isFormaleta && (
      <button
        type="button"
        onClick={() => setFiltersOpen(true)}
        title="Filtrar"
        aria-label="Filtrar"
        className="relative flex h-10 items-center gap-1.5 rounded px-2.5 text-sm hover:bg-brand-gray-400 sm:px-3"
      >
        <Funnel size={16} weight="bold" />
        <span className="hidden sm:inline">Filtrar</span>
        {activeFilterCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-green-100 px-1 font-mono text-[10px] font-bold text-brand-gray-500 sm:static sm:ml-1 sm:h-4 sm:w-4 sm:p-0">
            {activeFilterCount}
          </span>
        )}
      </button>
      )}

      {!isSitePlan && !isTramites && !isFormaleta && (
        // CTA primario: pill verde solido con sombra (bubble effect). En
        // mobile queda como circulo perfecto h-10 w-10; en sm+ se ensancha
        // con padding horizontal y muestra el label.
        <button
          type="button"
          onClick={() => setCreating(true)}
          title="Agregar tarea"
          aria-label="Agregar tarea"
          className="flex h-10 w-10 shrink-0 items-center justify-center gap-1.5 rounded-full bg-brand-green-100 text-sm font-semibold text-black shadow-md transition-colors hover:bg-brand-green-200 sm:w-auto sm:px-4"
        >
          <Plus size={18} weight="bold" />
          <span className="hidden sm:inline">Agregar</span>
        </button>
      )}
    </div>
    </div>
  );
}
